import { AlertTriangle, BellRing, Clock } from 'lucide-react';
import type { LastBroadcastInfo } from '../lib/api/breaking';
import { fmtLagos } from '../lib/date';

/**
 * Surfaces "this post was already broadcast N minutes ago" inside the
 * confirm dialogs of the breaking-news flow. The data comes from
 * `useLastBroadcast` — fetched on dialog open so the recency check is
 * always fresh.
 *
 * Three visual tiers:
 *   - **In-flight** (queued, not sent yet): blue/info — re-broadcasting
 *     will *cancel* the queued one and replace it. No duplicate to users.
 *   - **Recently sent** (within 24 h, default): amber/warning — re-broadcast
 *     will fire a SECOND notification to the same subscribers. Editors
 *     should usually NOT do this without a fresh angle.
 *   - **Sent long ago / cancelled**: muted info — kept visible so the
 *     editor knows the broadcast history exists, but no warning escalation.
 */
const RECENT_HOURS = 24;

interface PreviousBroadcastNoticeProps {
  info: LastBroadcastInfo;
}

export function PreviousBroadcastNotice({
  info,
}: PreviousBroadcastNoticeProps): JSX.Element | null {
  const now = Date.now();

  // In-flight: tells the editor what supersede will do — cancel the queued
  // one and queue/send a replacement. Reassuring, not warning.
  if (info.status === 'pending') {
    const ageMin = ageMinutes(info.scheduledAt, now);
    return (
      <NoticeCard tone="info" icon={Clock}>
        <strong className="text-ink">A push for this post is already queued</strong>{' '}
        ({info.manual ? 'manual' : info.triggerSource}, scheduled to fire{' '}
        {fmtLagos(info.scheduledAt, 'PP p')}
        {ageMin !== null ? ` — ${formatRelative(ageMin, 'in')}` : ''}).
        Re-broadcasting now will <strong className="text-ink">cancel that queued push</strong>{' '}
        and replace it with a fresh one. No duplicate notification.
      </NoticeCard>
    );
  }

  // Recently sent: real risk of subscribers getting two notifications.
  if (info.status === 'sent' && info.sentAt) {
    const ageMin = ageMinutes(info.sentAt, now);
    if (ageMin !== null && ageMin < RECENT_HOURS * 60) {
      return (
        <NoticeCard tone="warn" icon={AlertTriangle}>
          <strong className="text-ink">This post was already sent</strong>{' '}
          {formatRelative(ageMin, 'ago')} (
          {info.manual ? 'manual' : info.triggerSource}
          {info.deliveredCount != null
            ? `, delivered to ~${info.deliveredCount.toLocaleString()} devices`
            : ''}
          ). <strong className="text-ink">Re-broadcasting will deliver a SECOND notification</strong>{' '}
          to the same subscribers — only do this if there's a meaningful update worth
          interrupting them again.
        </NoticeCard>
      );
    }
    // Older than RECENT_HOURS — quieter info-only.
    return (
      <NoticeCard tone="muted" icon={BellRing}>
        Last sent {fmtLagos(info.sentAt, 'PP p')} ({info.manual ? 'manual' : info.triggerSource}
        {info.deliveredCount != null
          ? `, ~${info.deliveredCount.toLocaleString()} devices`
          : ''}
        ).
      </NoticeCard>
    );
  }

  // Cancelled — neutral context, no warning.
  if (info.status === 'cancelled' && info.cancelledAt) {
    return (
      <NoticeCard tone="muted" icon={BellRing}>
        A previous broadcast for this post was cancelled{' '}
        {fmtLagos(info.cancelledAt, 'PP p')}.
      </NoticeCard>
    );
  }

  return null;
}

function NoticeCard({
  tone,
  icon: Icon,
  children,
}: {
  tone: 'info' | 'warn' | 'muted';
  icon: typeof AlertTriangle;
  children: React.ReactNode;
}): JSX.Element {
  const styles =
    tone === 'warn'
      ? 'border-warning/40 bg-warning/10'
      : tone === 'info'
        ? 'border-brand/30 bg-brand-soft/30'
        : 'border-line bg-surface-tinted/50';
  const iconColor =
    tone === 'warn' ? 'text-warning' : tone === 'info' ? 'text-brand' : 'text-ink-subtle';
  return (
    <div className={`flex items-start gap-2 rounded-md border ${styles} px-3 py-2`}>
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconColor}`} />
      <p className="text-[12.5px] leading-snug text-ink-muted">{children}</p>
    </div>
  );
}

function ageMinutes(iso: string, now: number): number | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.round((now - t) / 60_000);
}

/** "in 8 minutes" / "12 minutes ago" / "3 hours ago". `direction` flips the
 *  sign of the unit phrase — minutes can be negative for `'in'` (queued for
 *  the future) or positive for `'ago'` (sent in the past). */
function formatRelative(ageMin: number, direction: 'ago' | 'in'): string {
  const abs = Math.abs(ageMin);
  if (abs < 60) return direction === 'ago' ? `${abs} min ago` : `in ${abs} min`;
  const h = Math.round(abs / 60);
  if (h < 24) return direction === 'ago' ? `${h} h ago` : `in ${h} h`;
  const d = Math.round(h / 24);
  return direction === 'ago' ? `${d} d ago` : `in ${d} d`;
}
